import { createSlice } from '@reduxjs/toolkit';

import { loadApp, updateApp, createApp } from './application.middleware';
import { logout } from '../auth/auth-middleware';
import { AxiosResponse, Application } from 'api';

type UpdatedApplicationState = {
    message: string | null;
    error: string | null;
    isLoading: boolean;
};

type NewApplicationState = {
    entity: Application | null;
    error: string | null;
    isLoading: boolean;
};

type ApplicationState = {
    entity: Application | null;
    error: string | null;
    isLoading: boolean;
    updatedApp: UpdatedApplicationState;
    newApp: NewApplicationState;
};

const initialState: ApplicationState = {
    entity: null,
    error: null,
    isLoading: false,
    updatedApp: {
        message: null,
        error: null,
        isLoading: false,
    },
    newApp: {
        entity: null,
        error: null,
        isLoading: false,
    },
};

export const applicationSlice = createSlice({
    name: 'app/application',
    initialState,
    reducers: {
        setActiveApp: (state, action) => {
            state.entity = action.payload;
            state.error = null;
            state.isLoading = false;
        },
        resetUpdatedApp: (state) => {
            state.updatedApp = {
                message: null,
                error: null,
                isLoading: false,
            };
        },
        resetNewApp: (state) => {
            state.newApp = {
                entity: null,
                error: null,
                isLoading: false,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // GET
            .addCase(loadApp.fulfilled, (state, action) => {
                state.entity = action.payload.data;
                state.error = null;
                state.isLoading = false;
            })
            .addCase(loadApp.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadApp.rejected, (state, action) => {
                state.error = action.error.message as string;
                state.entity = null;
                state.isLoading = false;
            })
            // PUT
            .addCase(updateApp.fulfilled, (state, action) => {
                state.updatedApp = {
                    message: action.payload.data.msg,
                    error: null,
                    isLoading: false,
                };
            })
            .addCase(updateApp.pending, (state) => {
                state.updatedApp = {
                    message: null,
                    error: null,
                    isLoading: true,
                };
            })
            .addCase(updateApp.rejected, (state, action) => {
                state.updatedApp = {
                    message: null,
                    error: action.error.message as string,
                    isLoading: false,
                };
                state.error = null;
                state.isLoading = false;
            })
            // POST
            .addCase(createApp.fulfilled, (state, action) => {
                state.newApp = {
                    entity: (action.payload as AxiosResponse<Application>).data,
                    error: null,
                    isLoading: false,
                };
            })
            .addCase(createApp.pending, (state) => {
                state.newApp.isLoading = true;
            })
            .addCase(createApp.rejected, (state, action) => {
                const errorMsg = (action.payload as { msg: string }).msg
                    ? (action.payload as { msg: string }).msg
                    : 'Something went wrong';
                state.newApp = {
                    entity: null,
                    error: errorMsg,
                    isLoading: false,
                };
            })
            .addCase(logout.fulfilled, (state) => {
                state.entity = null;
                state.error = null;
                state.isLoading = false;
                state.updatedApp = {
                    message: null,
                    error: null,
                    isLoading: false,
                };
                state.newApp = {
                    entity: null,
                    error: null,
                    isLoading: false,
                };
            });
    },
});

export const { setActiveApp, resetUpdatedApp, resetNewApp } = applicationSlice.actions;
export default applicationSlice.reducer;
